/**
 * Activity config descriptor registry.
 *
 * Each activity declares the fields a professional must configure
 * when adding it to a playbook. The generic ActivityConfigForm
 * renders the right inputs based on this descriptor — no per-game
 * switch cases needed in the editor.
 */
import { z } from 'zod'

export type ActivitySlug =
  | 'memory'
  | 'wordmatch'
  | 'cutlery'
  | 'wordsearch'
  | 'sudoku'
  | 'maths'
  | 'shoppinglist'
  | 'eurmoney'
  | 'pillbox'
  | 'attention'
  | 'symmetry'
  | 'whats_missing'
  | 'roman'
  | 'crosswords'
  | 'hours'
  | 'synonyms'
  | 'puzzle'
  | 'chess'
  | 'numtarget'

export type ConfigField =
  | { type: 'difficulty' }
  | {
      type: 'select'
      key: string
      labelEs: string
      labelEn: string
      options: { value: string; labelEs: string; labelEn: string }[]
    }
  | {
      type: 'multiselect'
      key: string
      labelEs: string
      labelEn: string
      options: { value: string; labelEs: string; labelEn: string }[]
      minSelected: number
    }
  | { type: 'theme' }

export interface ActivityConfigDescriptor {
  slug:          ActivitySlug
  fields:        ConfigField[]
  defaultConfig: Record<string, unknown>
}

// ── Per-activity descriptors ───────────────────────────────────

const memory: ActivityConfigDescriptor = {
  slug: 'memory',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'images-images', labelEs: 'Imágenes',          labelEn: 'Images' },
        { value: 'images-words',  labelEs: 'Imagen + Palabra',   labelEn: 'Image + Word' },
        { value: 'words-words',   labelEs: 'Palabras',           labelEn: 'Words' },
      ],
    },
    { type: 'theme' },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'images-images', theme: null },
}

const wordmatch: ActivityConfigDescriptor = {
  slug: 'wordmatch',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'images',      labelEs: 'Imágenes',     labelEn: 'Images' },
        { value: 'definitions', labelEs: 'Definiciones', labelEn: 'Definitions' },
        { value: 'anagram',     labelEs: 'Anagrama',     labelEn: 'Anagram' },
      ],
    },
    { type: 'theme' },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'images', theme: null },
}

const cutlery: ActivityConfigDescriptor = {
  slug: 'cutlery',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'drawer', labelEs: 'Cajón',     labelEn: 'Drawer' },
        { value: 'table',  labelEs: 'Mesa',       labelEn: 'Table' },
        { value: 'count',  labelEs: 'Contar',     labelEn: 'Count' },
      ],
    },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'drawer' },
}

const wordsearch: ActivityConfigDescriptor = {
  slug: 'wordsearch',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'simple',    labelEs: 'Simple',    labelEn: 'Simple' },
        { value: 'diagonals', labelEs: 'Diagonales', labelEn: 'Diagonals' },
        { value: 'reversed',  labelEs: 'Invertido',  labelEn: 'Reversed' },
      ],
    },
    { type: 'theme' },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'simple', theme: null },
}

const sudoku: ActivityConfigDescriptor = {
  slug: 'sudoku',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'standard', labelEs: 'Estándar (9×9)', labelEn: 'Standard (9×9)' },
        { value: 'mini',     labelEs: 'Mini (4×4)',      labelEn: 'Mini (4×4)' },
      ],
    },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'standard' },
}

const maths: ActivityConfigDescriptor = {
  slug: 'maths',
  fields: [
    { type: 'difficulty' },
    {
      type: 'multiselect', key: 'activeOps',
      labelEs: 'Operaciones', labelEn: 'Operations',
      minSelected: 1,
      options: [
        { value: 'addition',       labelEs: 'Suma (+)',            labelEn: 'Addition (+)' },
        { value: 'subtraction',    labelEs: 'Resta (−)',           labelEn: 'Subtraction (−)' },
        { value: 'multiplication', labelEs: 'Multiplicación (×)',  labelEn: 'Multiplication (×)' },
        { value: 'division',       labelEs: 'División (÷)',        labelEn: 'Division (÷)' },
      ],
    },
  ],
  defaultConfig: { difficulty: 'easy', activeOps: ['addition'] },
}

const shoppinglist: ActivityConfigDescriptor = {
  slug: 'shoppinglist',
  fields: [
    { type: 'difficulty' },
  ],
  defaultConfig: { difficulty: 'easy' },
}

const eurmoney: ActivityConfigDescriptor = {
  slug: 'eurmoney',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'identify', labelEs: 'Identificar',   labelEn: 'Identify' },
        { value: 'pay',      labelEs: 'Dar cambio',    labelEn: 'Make Change' },
        { value: 'count',    labelEs: 'Contar',        labelEn: 'Count' },
      ],
    },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'identify' },
}

const pillbox: ActivityConfigDescriptor = {
  slug: 'pillbox',
  fields: [
    { type: 'difficulty' },
  ],
  defaultConfig: { difficulty: 'easy' },
}

const attention: ActivityConfigDescriptor = {
  slug: 'attention',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'words',   labelEs: 'Palabras', labelEn: 'Words' },
        { value: 'numbers', labelEs: 'Números',  labelEn: 'Numbers' },
      ],
    },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'words' },
}

const symmetry: ActivityConfigDescriptor = {
  slug: 'symmetry',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'vertical',               labelEs: 'Simetría vertical',      labelEn: 'Vertical symmetry' },
        { value: 'horizontal',             labelEs: 'Simetría horizontal',    labelEn: 'Horizontal symmetry' },
        { value: 'quad',                   labelEs: 'Simetría cuádruple',     labelEn: 'Quad symmetry' },
        { value: 'translation-vertical',   labelEs: 'Traslación vertical',    labelEn: 'Vertical translation' },
        { value: 'translation-horizontal', labelEs: 'Traslación horizontal',  labelEn: 'Horizontal translation' },
      ],
    },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'vertical' },
}

const whats_missing: ActivityConfigDescriptor = {
  slug: 'whats_missing',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'emoji',  labelEs: 'Emojis', labelEn: 'Emojis' },
        { value: 'shapes', labelEs: 'Formas', labelEn: 'Shapes' },
      ],
    },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'emoji' },
}

const roman: ActivityConfigDescriptor = {
  slug: 'roman',
  fields: [
    { type: 'difficulty' },
  ],
  defaultConfig: { difficulty: 'easy' },
}

const crosswords: ActivityConfigDescriptor = {
  slug: 'crosswords',
  fields: [
    { type: 'difficulty' },
  ],
  defaultConfig: { difficulty: 'easy' },
}

const hours: ActivityConfigDescriptor = {
  slug: 'hours',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'analog',           labelEs: 'Analógico',           labelEn: 'Analog' },
        { value: 'digital',          labelEs: 'Digital',             labelEn: 'Digital' },
        { value: 'analog-reverse',   labelEs: 'Analógico (inverso)', labelEn: 'Analog (reverse)' },
        { value: 'digital-reverse',  labelEs: 'Digital (inverso)',   labelEn: 'Digital (reverse)' },
      ],
    },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'analog' },
}

const synonyms: ActivityConfigDescriptor = {
  slug: 'synonyms',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'synonyms',  labelEs: 'Sinónimos',  labelEn: 'Synonyms' },
        { value: 'antonyms',  labelEs: 'Antónimos',  labelEn: 'Antonyms' },
        { value: 'mixed',     labelEs: 'Mixto',      labelEn: 'Mixed' },
      ],
    },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'synonyms' },
}

const puzzle: ActivityConfigDescriptor = {
  slug: 'puzzle',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'puzzle', labelEs: 'Puzzle',     labelEn: 'Puzzle' },
        { value: 'pairs',  labelEs: 'Parejas',    labelEn: 'Pairs' },
      ],
    },
    { type: 'theme' },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'puzzle', theme: null },
}

const chess: ActivityConfigDescriptor = {
  slug: 'chess',
  fields: [
    { type: 'difficulty' },
    {
      type: 'select', key: 'mode',
      labelEs: 'Modo', labelEn: 'Mode',
      options: [
        { value: 'recognize', labelEs: 'Reconocer piezas', labelEn: 'Recognize pieces' },
        { value: 'movement',  labelEs: 'Movimientos',      labelEn: 'Movements' },
        { value: 'capture',   labelEs: 'Captura',          labelEn: 'Capture' },
        { value: 'puzzle',    labelEs: 'Puzzles',          labelEn: 'Puzzles' },
      ],
    },
  ],
  defaultConfig: { difficulty: 'easy', mode: 'recognize' },
}

// ── Registry ───────────────────────────────────────────────────

const numtarget: ActivityConfigDescriptor = {
  slug: 'numtarget',
  fields: [
    { type: 'difficulty' },
  ],
  defaultConfig: { difficulty: 'easy' },
}

export const ACTIVITY_DESCRIPTORS: Record<ActivitySlug, ActivityConfigDescriptor> = {
  memory,
  wordmatch,
  cutlery,
  wordsearch,
  sudoku,
  maths,
  shoppinglist,
  eurmoney,
  pillbox,
  attention,
  symmetry,
  whats_missing,
  roman,
  crosswords,
  hours,
  synonyms,
  puzzle,
  chess,
  numtarget,
}

// ── Config types ──────────────────────────────────────────────

export const playbookItemConfigSchema = z.object({
  difficulty: z.enum(['easy', 'medium', 'hard']),
  mode:       z.string().optional(),
  activeOps:  z.array(z.string()).optional(),
  theme:      z.object({ id: z.number(), slug: z.string() }).nullable().optional(),
})

export type PlaybookItemConfig = z.infer<typeof playbookItemConfigSchema>
